import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { CstIbsCbsStruct } from '../../../../../../shared/services/structs/motor-fiscal/cst-ibs-cbs.struct';
import { CClassTribIbsCbsStruct } from '../../../../../../shared/services/structs/motor-fiscal/c-class-trib-ibs-cbs.struct';
import { NaturezaOperacaoRegraTributariaStruct } from '../../../../../../shared/services/structs/motor-fiscal/natureza-operacao-regra-tributaria.struct';
import { LookupItemStruct } from '../../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { ModeloDocumento } from '../../../../../../shared/services/models/dfe/modelo-documento.model';
import { TipoOperacaoEnum } from '../../../../../../shared/enums/motor-fiscal/tipo-operacao.enum';
import { formatNcm, badgeLabel } from '../../regra-tributaria-escopo.util';

@Component({
    selector: 'app-regra-tributaria-revisao-step',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, NgxMaskPipe, EmptyStateComponent],
    providers: [provideNgxMask()],
    templateUrl: './regra-tributaria-revisao-step.component.html',
    styleUrl: './regra-tributaria-revisao-step.component.css',
})
export class RegraTributariaRevisaoStepComponent {
    @Input({ required: true }) form!: FormGroup;
    @Input() csts: CClassTribIbsCbsStruct[] = [];
    @Input() cclasstribs: CClassTribIbsCbsStruct[] = [];
    @Input() modelos: ModeloDocumento[] = [];
    @Input() listaRegimeTributario: LookupItemStruct[] = [];
    @Input() listaTipoTributario: LookupItemStruct[] = [];
    @Input() naturezasSelecionadas: NaturezaOperacaoRegraTributariaStruct[] = [];
    @Output() editarEtapa = new EventEmitter<number>();

    public readonly formatNcm = formatNcm;
    public readonly badgeLabel = badgeLabel;

    public readonly TipoOperacaoEnum = TipoOperacaoEnum;
    public readonly cfopMaskPatterns = { '0': { pattern: /[0-9?]/ } };
    public readonly tipoOperacaoLabel: Record<number, string> = {
        [TipoOperacaoEnum.Entrada]: 'Entrada',
        [TipoOperacaoEnum.Saida]: 'Saída',
    };
    // "true"/"false": mesma representação usada no escopo (ver regra-tributaria-escopo-step).
    private readonly contribLabel: Record<string, string> = {
        'true': 'Contribuinte',
        'false': 'Não contribuinte',
    };

    public get formValue(): any {
        return this.form.getRawValue();
    }

    public get cstDescricao(): string {
        const codigo = this.formValue.classificacao?.cstIbsCbs;
        const item = this.csts.find(x => x.codigo === codigo);
        return item ? `${item.codigo} - ${item.descricao}` : (codigo || 'Não informado');
    }

    public get cclassDescricao(): string {
        const codigo = this.formValue.classificacao?.cclassTrib;
        const item = this.cclasstribs.find(x => x.codigo === codigo);
        return item ? `${item.codigo} - ${item.descricao}` : (codigo || 'Não informado');
    }

    public get temCst(): boolean {
        return !!this.formValue.classificacao?.cstIbsCbs;
    }

    public get temCclass(): boolean {
        return !!this.formValue.classificacao?.cclassTrib;
    }

    /** Modelos de documento selecionados (mapeados por código via lookup). */
    public get modelosSelecionados(): string[] {
        const codigos: string[] = this.formValue.modelosDocumento ?? [];
        return codigos.map(c => {
            const modelo = this.modelos.find(m => m.codigo === c);
            return modelo ? `${modelo.nome} - ${modelo.descricao}` : c;
        });
    }

    public get gruposTributacaoAtivos(): string[] {
        const v = this.formValue;
        const grupos = [
            { ativo: v.ibsCbs?.isAtivo, label: 'IBS/CBS' },
            { ativo: v.tributacaoRegular?.isAtivo, label: 'Tributação Regular' },
            { ativo: v.mono?.isAtivo, label: 'Tributação Monofásica' },
            { ativo: v.creditoPresumido?.isAtivo, label: 'Crédito Presumido' },
            { ativo: v.impostoSeletivo?.isAtivo, label: 'Imposto Seletivo' },
        ];
        return grupos.filter(g => g.ativo).map(g => g.label);
    }

    public get escopoValue(): any {
        return this.formValue.escopo ?? {};
    }

    public labelContrib(id: string): string {
        return this.contribLabel[id] ?? String(id);
    }

    public labelLookup(id: number, lista: LookupItemStruct[]): string {
        return lista.find(x => x.id === id)?.descricao ?? String(id);
    }
}
